import {get} from 'lodash';

import {ComparisonCondition, MigrationStep} from '@/model/types';
import {MigrationStepCondition, StepPredicate} from '@/model/types/migrations/condition';
import {applyForField} from '../../utils/object';
import {compareValues} from '../../utils/predicate';

type Condition = MigrationStepCondition;

type PredicateOperator = Condition['operator'];

type StepPredicateFactory<T extends PredicateOperator> = (
  condition: Omit<Extract<Condition, {operator: T}>, 'operator'>,
) => StepPredicate;

type StepPredicateMap = {
  [T in PredicateOperator]: StepPredicateFactory<T>;
};

const createComparisonStepPredicateFactory = <T extends ComparisonCondition['operator']>(
  operator: T,
): StepPredicateFactory<T> => {
  return condition => {
    return data => {
      return applyForField(data, condition.field, path => {
        return compareValues(get(data, path), condition.value, operator);
      }).every(Boolean);
    };
  };
};

const AndStepPredicate: StepPredicateFactory<'and'> = condition => {
  return data => condition.conditions.every(c => getStepPredicate(c)(data));
};

const OrStepPredicate: StepPredicateFactory<'or'> = condition => {
  return data => condition.conditions.some(c => getStepPredicate(c)(data));
};

const NotStepPredicate: StepPredicateFactory<'not'> = condition => {
  return data => !getStepPredicate({operator: 'and', conditions: condition.conditions})(data);
};

const InStepPredicate: StepPredicateFactory<'in'> = condition => {
  return data => condition.value.some(v => getStepPredicate({field: condition.field, operator: '=', value: v})(data));
};

const NotInStepPredicate: StepPredicateFactory<'not in'> = condition => {
  return data => !getStepPredicate({...condition, operator: 'in'})(data);
};

export const stepPredicateMap: StepPredicateMap = {
  '=': createComparisonStepPredicateFactory('='),
  '!=': createComparisonStepPredicateFactory('!='),
  '>': createComparisonStepPredicateFactory('>'),
  '>=': createComparisonStepPredicateFactory('>='),
  '<': createComparisonStepPredicateFactory('<'),
  '<=': createComparisonStepPredicateFactory('<='),
  'and': AndStepPredicate,
  'or': OrStepPredicate,
  'not': NotStepPredicate,
  'in': InStepPredicate,
  'not in': NotInStepPredicate,
};

export const getStepPredicate = <K extends PredicateOperator>(
  condition: Extract<MigrationStepCondition, {operator: K}>,
): StepPredicate => {
  if (!stepPredicateMap[condition.operator]) {
    throw new Error(`Unknown condition operator: ${condition.operator}`);
  }

  return stepPredicateMap[condition.operator](condition);
};

export const createStepPredicate = ({conditions}: MigrationStep): StepPredicate => {
  return data => {
    if (!conditions || !conditions.length) return true;
    return conditions.every(condition => getStepPredicate(condition)(data));
  };
};
